import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import * as Yup from "yup";
import Badge from "../../../library/Badge";
import Banner from "../../../library/Banner";
import SelectDropdown from "../../../library/SelectDropdown";
import StickyFooter from "../../../library/StickyFooter";
import TextInput from "../../../library/TextInput";
import { createProgrammeStudyPath, updateProgrammeStudyPath } from "../../../../services/ProgramService";
import {
    invalidateStudyPathQueries,
    useStudyPathModuleList,
    useStudyPathPhaseDetails,
    useStudyPathUnassignedModules,
} from "../../../../query/hooks/QueryHooksStudyPaths";
import { useUnsavedChangesGuard } from "../../../../hooks/useUnsavedChangesGuard";
import AssignModulesModal from "./AssignModulesModal";
import {
    getGroupForModuleType,
    getSemesterTermOptions,
    mergeVisibleUnassignedModules,
    MODULE_GROUPS,
    normalizeModuleRows,
    sumModuleCredits,
    YEAR_OPTIONS,
} from "./studyPaths.utils";
import PhaseModuleGroupsTable from "./PhaseModuleGroupsTable";
import UnassignedModulesPanel from "./UnassignedModulesPanel";
import { useStagedPhaseModules } from "./useStagedPhaseModules";
import useStudyPathPermissions from "./useStudyPathPermissions";
import { StyledFormCard, StyledFormFieldsRow, StyledPageStack, StyledSectionHeading } from "./studyPaths.styles";

/**
 * Empty rather than 0 so an untouched group shows the "Credits Required" placeholder from SD-3;
 * the submit handler coerces it back to 0, which the API requires.
 */
const emptyGroupCredits = { core: "", foundation: "", elective: "" };

/**
 * Open or create a phase: Phase Name/Year/Semester-Term, the three module groups, the
 * Unassigned Modules panel, and Save/Cancel.
 * @param {string | number} props.id PMC programme id.
 * @param {object} [props.versionData] Programme version record, for its `lock_version_settings`.
 * @param {object} [props.detail] The PMC programme detail record, for its `Term_Period`.
 */
function PhaseForm({ id: programId, versionData, detail }) {
    const history = useHistory();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const queryType = query.get("type");
    const phaseId = query.get("id");
    const queryVersionId = query.get("versionId");
    const queryProgrammeId = query.get("programmeId") || programId;
    const isEdit = queryType === "open" && Boolean(phaseId);

    const isLocked = Boolean(versionData?.lock_version_settings);
    const disabledReason = isLocked ? "Unlock the version to make changes." : undefined;

    const { canAdd, canEdit, canEditModules, noPermissionReason } = useStudyPathPermissions();

    const canSave = isEdit ? canEdit : canAdd;
    const assignDisabled = isLocked || !canEditModules;
    const assignDisabledReason = isLocked ? disabledReason : (canEditModules ? undefined : noPermissionReason);
    const detailsDisabledReason = isLocked ? disabledReason : (canSave ? undefined : noPermissionReason);
    const detailsDisabled = isLocked || !canSave;

    const [isSaving, setIsSaving] = useState(false);
    const [assignModalGroup, setAssignModalGroup] = useState(null);

    const { data: phaseDetails } = useStudyPathPhaseDetails(isEdit ? phaseId : null);

    const semesterTermOptions = getSemesterTermOptions(detail?.Term_Period);

    const groupQueries = {
        core: useStudyPathModuleList({ id: phaseId, isAssigned: 1, module_type: 1, programme_id: queryProgrammeId, version_id: queryVersionId }, isEdit),
        foundation: useStudyPathModuleList({ id: phaseId, isAssigned: 1, module_type: 2, programme_id: queryProgrammeId, version_id: queryVersionId }, isEdit),
        elective: useStudyPathModuleList({ id: phaseId, isAssigned: 1, module_type: 3, programme_id: queryProgrammeId, version_id: queryVersionId }, isEdit),
    };

    const groupsUnresolved = MODULE_GROUPS.some((group) => {
        const groupQuery = groupQueries[group.key];
        return groupQuery.isLoading || groupQuery.isError;
    });

    const hydrateFrom = !isEdit
        ? { core: [], foundation: [], elective: [] }
        : (groupsUnresolved ? null : {
            core: groupQueries.core.data.data,
            foundation: groupQueries.foundation.data.data,
            elective: groupQueries.elective.data.data,
        });

    const {
        stagedModules,
        stageModule,
        unstageModule,
        isDirty: modulesDirty,
        isHydrated,
    } = useStagedPhaseModules({
        groups: MODULE_GROUPS,
        hydrateFrom,
        resetKey: isEdit ? phaseId : undefined,
        getModuleId: (module) => module.Id,
    });

    const {
        data: unassignedModules,
        isLoading: unassignedLoading,
        isError: unassignedError,
    } = useStudyPathUnassignedModules(queryProgrammeId, queryVersionId);

    const visibleUnassignedModules = mergeVisibleUnassignedModules(unassignedModules, stagedModules, hydrateFrom, (module) => module.Id);

    const groupEntries = MODULE_GROUPS.map((group) => {
        const modules = stagedModules[group.key];
        return {
            group,
            modules,
            loading: isEdit && !isHydrated,
            suppliedCredits: sumModuleCredits(normalizeModuleRows(modules)),
        };
    });

    const goToList = () => {
        history.push(`/program-management-center/programmes/versions/study_path?type=table&versionId=${queryVersionId}&programmeId=${queryProgrammeId}`);
    };

    const goToPhase = (newPhaseId) => {
        history.push(`/program-management-center/programmes/versions/study_path?type=open&id=${newPhaseId}&versionId=${queryVersionId}&programmeId=${queryProgrammeId}`);
    };

    const creditSchemaFor = (entry) => Yup.mixed().test(
        "credit-ceiling",
        `This group's modules only supply ${entry.suppliedCredits} credits. Assign more modules or lower the target.`,
        (value) => (Number(value) || 0) <= entry.suppliedCredits
    );

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            phaseName: phaseDetails?.name || "",
            year: phaseDetails?.year ? String(phaseDetails.year) : "",
            semesterTerm: phaseDetails?.semester_term ? String(phaseDetails.semester_term) : "",
            core: phaseDetails?.core_credits ?? emptyGroupCredits.core,
            foundation: phaseDetails?.foundational_credits ?? emptyGroupCredits.foundation,
            elective: phaseDetails?.elective_credits ?? emptyGroupCredits.elective,
        },
        validationSchema: Yup.object({
            phaseName: Yup.string().trim().required("Phase name is required."),
            year: Yup.string().required("Select a year"),
            semesterTerm: Yup.string().required("Select a semester or term"),
            core: creditSchemaFor(groupEntries[0]),
            foundation: creditSchemaFor(groupEntries[1]),
            elective: creditSchemaFor(groupEntries[2]),
        }),
        onSubmit: async (values) => {
            if (isEdit && groupsUnresolved) {
                Swal.fire("Please wait", "This phase's modules are still loading - try saving again in a moment.", "info");
                return;
            }

            setIsSaving(true);
            try {
                const payload = {
                    name: values.phaseName,
                    programme_id: queryProgrammeId,
                    version_id: queryVersionId,
                    year: values.year,
                    semester_term: values.semesterTerm,
                    core_credits: Number(values.core) || 0,
                    foundational_credits: Number(values.foundation) || 0,
                    elective_credits: Number(values.elective) || 0,
                    core_modules: groupEntries[0].modules.map((module) => module.Id),
                    foundational_modules: groupEntries[1].modules.map((module) => module.Id),
                    elective_modules: groupEntries[2].modules.map((module) => module.Id),
                };

                if (isEdit) {
                    await updateProgrammeStudyPath({ ...payload, id: phaseId });
                    await invalidateStudyPathQueries(queryProgrammeId, queryVersionId, phaseId);
                    Swal.fire("Saved!", "The phase has been saved.", "success");
                    goToList();
                    return;
                }

                const response = await createProgrammeStudyPath(payload);
                await invalidateStudyPathQueries(queryProgrammeId, queryVersionId);
                Swal.fire("Saved!", "The phase has been saved. You can now assign modules to it.", "success");
                if (response?.data?.id) {
                    goToPhase(response.data.id);
                    return;
                }
                goToList();
            } catch (error) {
                console.log("PhaseForm.js->handleSave->Error", error);
                const responseMessage = error?.response?.data?.message;
                const text = Array.isArray(responseMessage)
                    ? responseMessage.map((entry) => entry.message).join(" ")
                    : responseMessage;
                Swal.fire("Error", text || "Something went wrong.", "error");
            } finally {
                setIsSaving(false);
            }
        },
    });

    const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
    const fieldErrors = hasAttemptedSave ? formik.errors : {};

    const handleSaveClick = async () => {
        setHasAttemptedSave(true);
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            Swal.fire("Missing information", "Please fill in the highlighted fields before saving.", "warning");
            return;
        }
        formik.handleSubmit();
    };

    const handleUnlinkModule = (module) => unstageModule(module.id ?? module.Id);

    const handleAssignUnassignedModule = (module) => {
        const moduleGroup = getGroupForModuleType(module.type);
        stageModule(moduleGroup ? moduleGroup.key : "core", module);
    };

    useUnsavedChangesGuard(formik.dirty || modulesDirty, true, history);

    return (
        <StyledPageStack>
            {isLocked && (
                <Banner tone="locked" badge={<Badge tone="warningSolid" icon="lock">Locked</Badge>}>
                    Study path phases are currently read-only. Creating, editing, re-arranging and
                    module assignment are disabled. Unlock the version to make changes.
                </Banner>
            )}

            <StyledFormCard>
                <StyledSectionHeading>Study Path Phase</StyledSectionHeading>
                <StyledFormFieldsRow>
                    <TextInput
                        label="Phase Name"
                        required
                        name="phaseName"
                        value={formik.values.phaseName}
                        onChange={formik.handleChange}
                        errorText={fieldErrors.phaseName}
                        disabled={detailsDisabled}
                        disabledReason={detailsDisabledReason}
                    />
                    <SelectDropdown
                        label="Year"
                        required
                        name="year"
                        placeholder="Select year"
                        value={formik.values.year}
                        onChange={formik.handleChange}
                        options={YEAR_OPTIONS}
                        errorText={fieldErrors.year}
                        disabled={detailsDisabled}
                        disabledReason={detailsDisabledReason}
                    />
                    <SelectDropdown
                        label="Semester/Term"
                        required
                        name="semesterTerm"
                        placeholder="Select semester or term"
                        value={formik.values.semesterTerm}
                        onChange={formik.handleChange}
                        options={semesterTermOptions}
                        errorText={fieldErrors.semesterTerm}
                        disabled={detailsDisabled}
                        disabledReason={detailsDisabledReason}
                    />
                </StyledFormFieldsRow>
            </StyledFormCard>

            <PhaseModuleGroupsTable
                groups={groupEntries}
                requiredCredits={{ core: formik.values.core, foundation: formik.values.foundation, elective: formik.values.elective }}
                creditErrors={{ core: fieldErrors.core, foundation: fieldErrors.foundation, elective: fieldErrors.elective }}
                onRequiredCreditsChange={(groupKey, value) => formik.setFieldValue(groupKey, value)}
                onAssignModule={(group) => setAssignModalGroup(group)}
                onOpenModule={(module) => window.open(`/program-management-center/modules/module/open/${module.id || module.Id}`, "_blank")}
                onUnlinkModule={handleUnlinkModule}
                creditsDisabled={detailsDisabled}
                creditsDisabledReason={detailsDisabledReason}
                assignDisabled={assignDisabled}
                assignDisabledReason={assignDisabledReason}
            />

            <UnassignedModulesPanel
                modules={visibleUnassignedModules}
                loading={unassignedLoading}
                error={unassignedError}
                onAssign={handleAssignUnassignedModule}
                disabled={assignDisabled}
                disabledReason={assignDisabledReason}
            />

            <StickyFooter
                onSave={handleSaveClick}
                onCancel={goToList}
                isLoading={isSaving}
                saveDisabled={isLocked || !canSave || (isEdit && (groupsUnresolved || !isHydrated))}
            />

            {assignModalGroup && (
                <AssignModulesModal
                    phaseId={phaseId}
                    moduleType={assignModalGroup.type}
                    programmeId={queryProgrammeId}
                    versionId={queryVersionId}
                    onClose={() => setAssignModalGroup(null)}
                    onStageAssign={(module) => stageModule(assignModalGroup.key, module)}
                    onStageUnassign={(module) => unstageModule(module.Id)}
                    stagedModuleIds={stagedModules[assignModalGroup.key].map((module) => module.Id)}
                    disabled={assignDisabled}
                    disabledReason={assignDisabledReason}
                />
            )}
        </StyledPageStack>
    );
}

PhaseForm.displayName = "PhaseForm";

export default PhaseForm;
